import * as THREE from 'three';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const textureLoader = new THREE.TextureLoader();
textureLoader.crossOrigin = 'anonymous';

// Cache loaded textures by URL
const textureCache = new Map();

// Store the renderer's max anisotropy (set by the first preview component that initializes)
let maxAnisotropy = 16;

/**
 * Set the max anisotropy from the renderer capabilities.
 * Call this once after creating the WebGLRenderer.
 */
export function setMaxAnisotropy(renderer) {
  maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
}

// Preload foam texture on module load so it's instant when needed
const preloadedFoamImg = new Image();
preloadedFoamImg.crossOrigin = 'anonymous';
preloadedFoamImg.src = '/foam-texture.png';

/**
 * Configure a texture for seamless tiling with smooth blending.
 */
function configureTexture(texture) {
  texture.wrapS = THREE.MirroredRepeatWrapping;
  texture.wrapT = THREE.MirroredRepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = maxAnisotropy;
}

/**
 * Remap UV coordinates using weighted triplanar blending.
 * Computes UVs for all 3 projection planes (XY, XZ, YZ) and blends them
 * based on vertex normals. This avoids both UV seam lines (from hard-branching)
 * and side-face squishing (from flat projection).
 *
 * @param {THREE.BufferGeometry} geometry
 * @param {number} sx - Mesh scale X
 * @param {number} sy - Mesh scale Y (typically thickness)
 * @param {number} sz - Mesh scale Z
 * @param {number} tileSizeM - Physical size of one pattern tile
 */
export function remapUVsToWorldSpace(geometry, sx, sy, sz, tileSizeM = 0.8) {
  const uvAttr = geometry.attributes.uv;
  const posAttr = geometry.attributes.position;
  if (!uvAttr || !posAttr) return;

  if (!geometry.attributes.normal) geometry.computeVertexNormals();
  const nAttr = geometry.attributes.normal;
  const inv = 1 / tileSizeM;

  for (let i = 0; i < uvAttr.count; i++) {
    const wx = posAttr.getX(i) * sx;
    const wy = posAttr.getY(i) * sy;
    const wz = posAttr.getZ(i) * sz;

    let wX = Math.abs(nAttr.getX(i));
    let wY = Math.abs(nAttr.getY(i));
    let wZ = Math.abs(nAttr.getZ(i));

    // Sharpen blend weights aggressively to eliminate pattern compression at rounded edges
    const p = 16;
    wX = Math.pow(wX, p); wY = Math.pow(wY, p); wZ = Math.pow(wZ, p);
    const wSum = wX + wY + wZ || 1;
    wX /= wSum; wY /= wSum; wZ /= wSum;

    // X-facing → YZ plane, Y-facing → XZ plane, Z-facing → XY plane
    const u = wX * (wy * inv) + wY * (wx * inv) + wZ * (wx * inv);
    const v = wX * (wz * inv) + wY * (wz * inv) + wZ * (wy * inv);
    uvAttr.setXY(i, u, v);
  }
  uvAttr.needsUpdate = true;
}

/**
 * Remap UVs on an ExtrudeGeometry using weighted triplanar blending.
 * Computes UVs for all 3 projection planes and blends based on vertex normals.
 * Eliminates both UV seam lines and side-face squishing.
 *
 * @param {THREE.BufferGeometry} geometry
 * @param {number} tileSizeM - Physical size of one pattern tile
 */
export function remapExtrudeUVs(geometry, tileSizeM = 0.8, rotateUV = false) {
  const uvAttr = geometry.attributes.uv;
  const posAttr = geometry.attributes.position;
  if (!uvAttr || !posAttr) return;

  if (!geometry.attributes.normal) geometry.computeVertexNormals();
  const nAttr = geometry.attributes.normal;
  const inv = 1 / tileSizeM;

  for (let i = 0; i < uvAttr.count; i++) {
    const px = posAttr.getX(i);
    const py = posAttr.getY(i);
    const pz = posAttr.getZ(i);

    let wX = Math.abs(nAttr.getX(i));
    let wY = Math.abs(nAttr.getY(i));
    let wZ = Math.abs(nAttr.getZ(i));

    // Sharpen blend weights aggressively to eliminate pattern compression at rounded edges
    const p = 16;
    wX = Math.pow(wX, p); wY = Math.pow(wY, p); wZ = Math.pow(wZ, p);
    const wSum = wX + wY + wZ || 1;
    wX /= wSum; wY /= wSum; wZ /= wSum;

    // X-facing → YZ plane, Y-facing → XZ plane, Z-facing → XY plane
    const u = wX * (py * inv) + wY * (px * inv) + wZ * (px * inv);
    const v = wX * (pz * inv) + wY * (pz * inv) + wZ * (py * inv);
    uvAttr.setXY(i, rotateUV ? v : u, rotateUV ? u : v);
  }
  uvAttr.needsUpdate = true;
}

/**
 * Upscale a small image to a power-of-2 canvas for better mipmap generation.
 * Small source images produce too few mipmap levels, causing moiré on mobile.
 */
function upscaleToPOT(image) {
  const w = image.naturalWidth || image.width;
  const h = image.naturalHeight || image.height;
  const targetSize = 512;
  if (w >= targetSize && h >= targetSize) return null; // already large enough
  const canvas = document.createElement('canvas');
  canvas.width = targetSize;
  canvas.height = targetSize;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, targetSize, targetSize);
  return canvas;
}

/**
 * Load a fabric texture from an image URL.
 * Local URLs (/api/fabric-images/...) are loaded directly.
 * Warwick URLs are loaded via proxy.
 */
export function loadFabricTexture(imageUrl) {
  if (!imageUrl) return Promise.resolve(null);

  if (textureCache.has(imageUrl)) {
    return Promise.resolve(textureCache.get(imageUrl).clone());
  }

  let loadUrl;
  if (imageUrl.startsWith('/api/')) {
    loadUrl = `${API.replace('/api', '')}${imageUrl}`;
  } else if (imageUrl.includes('warwick.com.au')) {
    loadUrl = `${API}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
  } else {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const upscaled = upscaleToPOT(img);
      let texture;
      if (upscaled) {
        texture = new THREE.CanvasTexture(upscaled);
      } else {
        texture = new THREE.Texture(img);
        texture.needsUpdate = true;
      }
      configureTexture(texture);
      textureCache.set(imageUrl, texture);
      resolve(texture.clone());
    };
    img.onerror = () => resolve(null);
    img.src = loadUrl;
  });
}

/**
 * Apply or update fabric texture on a mesh material.
 * - If imageUrl is a foam texture path, applies foam grain over solid color.
 * - If imageUrl is a fabric URL, loads and applies texture.
 * - If not, removes texture and uses solid color.
 * - zoom controls how many times the texture repeats (higher = more tiled).
 */
export function applyFabricTexture(mesh, imageUrl, hexColor, zoom = 3, onLoaded = null) {
  if (!mesh || !mesh.material) { if (onLoaded) onLoaded(); return; }

  const mat = mesh.material;

  // Foam texture: mustard base with subtle foam grain overlay
  if (imageUrl && imageUrl.startsWith('/foam-texture')) {
    const applyFoam = (img) => {
      const s = 512;
      const c = document.createElement('canvas');
      c.width = s; c.height = s;
      const ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, s, s);
      const id = ctx.getImageData(0, 0, s, s);
      const d = id.data;
      for (let i = 0; i < d.length; i += 4) {
        d[i] = 255 - d[i];
        d[i+1] = 255 - d[i+1];
        d[i+2] = 255 - d[i+2];
      }
      ctx.putImageData(id, 0, 0);
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = hexColor || '#d4a843';
      ctx.fillRect(0, 0, s, s);
      const texture = new THREE.CanvasTexture(c);
      configureTexture(texture);
      texture.repeat.set(zoom, zoom);
      mat.map = texture;
      mat.color.set(0xffffff);
      if (mat.roughness !== undefined) mat.roughness = 0.92;
      mat.needsUpdate = true;
    };
    if (preloadedFoamImg.complete && preloadedFoamImg.naturalWidth > 0) {
      applyFoam(preloadedFoamImg);
    } else {
      preloadedFoamImg.onload = () => applyFoam(preloadedFoamImg);
    }
    if (onLoaded) onLoaded();
    return;
  }

  if (imageUrl && (imageUrl.includes('warwick.com.au') || imageUrl.startsWith('/api/fabric-images'))) {
    loadFabricTexture(imageUrl).then((texture) => {
      if (!texture) {
        mat.map = null;
        mat.color.set(hexColor || '#5a8a5a');
        mat.needsUpdate = true;
        if (onLoaded) onLoaded();
        return;
      }
      configureTexture(texture);
      texture.repeat.set(zoom, zoom);
      mat.map = texture;
      mat.color.set(0xffffff);
      mat.needsUpdate = true;
      if (onLoaded) onLoaded();
    });
  } else {
    if (mat.map) {
      mat.map = null;
      mat.needsUpdate = true;
    }
    mat.color.set(hexColor || '#5a8a5a');
    if (onLoaded) onLoaded();
  }
}

/**
 * Update texture repeat (zoom) on a mesh that already has a texture applied.
 */
export function updateTextureZoom(mesh, zoom) {
  if (!mesh?.material?.map) return;
  mesh.material.map.repeat.set(zoom, zoom);
  mesh.material.needsUpdate = true;
}
